import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import useInitStore from './init';
import { formatAddress } from '../composables/formatter';
import type {
  StepCustomerData,
  StepMphData,
  StepOrdererData,
  StepOrderRequestData,
  StepOwnerData,
  StepProductData,
} from '../types/step-data';

const useOrderStore = defineStore('order', () => {
  const initStore = useInitStore();

  const orderRequestData = ref<StepOrderRequestData>();
  const ordererData = ref<StepOrdererData>();
  const mphData = ref<StepMphData>();
  const productData = ref<StepProductData>();
  const customerData = ref<StepCustomerData>();
  const ownerData = ref<StepOwnerData>();
  const netzLeadIdNumber = ref<string | null>();

  /**
   * Netz lead ID prefix is based on the zip code and the current year:
   * ID-<first two digits of the zip code><last two digits of the year>-
   */
  const netzLeadIdPrefix = computed<string>(() => {
    if (!mphData.value) {
      return '';
    }
    const zip = initStore.paramsAddress!.zipCode;
    const year = new Date().getFullYear().toString();
    return `ID-${zip.substring(0, 2)}${year.substring(2, 4)}-`;
  });

  /**
   * The sales partner e-mail field is being set in the summary step,
   * but semantically it is a part of the MPH data.
   */
  const salesPartnerEmailAddress = computed<string | null>({
    get: () => mphData.value?.salesPartnerEmailAddress ?? null,
    set: value => {
      if (mphData.value) {
        mphData.value.salesPartnerEmailAddress = value ?? null;
      }
    },
  });

  const netzLeadId = computed<string | null>({
    get: () => netzLeadIdNumber.value ?? null,
    set: (value: string | null) => {
      netzLeadIdNumber.value = value;
      if (mphData.value) {
        mphData.value.netzLeadId = value ? netzLeadIdPrefix.value + value : null;
      }
    },
  });

  const isOrderingNe4 = computed<boolean>(
    () => orderRequestData.value?.isMphOrder === true && !!mphData.value,
  );

  const isOrderingProduct = computed<boolean>(
    () => orderRequestData.value?.bookingWithProduct === true && !!productData.value,
  );

  const isNe4OrderCompleteOrEndAssembly = computed<boolean>(
    () => !!orderRequestData.value?.isMphOrder
      && ['complete', 'endAssembly'].includes(orderRequestData.value?.ne4Order ?? ''),
  );

  const ordererAddress = computed<string>(() => {
    if (!ordererData.value) {
      return '';
    }
    return formatAddress(ordererData.value.address);
  });

  const mphTotalUnits = computed<number>(() => {
    if (!mphData.value) {
      return 0;
    }
    return mphData.value.numberOfResidentialUnits + mphData.value.numberOfCommercialUnits;
  });

  function addOrderRequestData(data: StepOrderRequestData) {
    orderRequestData.value = data;
  }
  function addOrdererData(data: StepOrdererData) {
    ordererData.value = data;
  }
  function addMphData(data: StepMphData) {
    mphData.value = data;
  }
  function addProductData(data: StepProductData) {
    productData.value = data;
  }
  function addCustomerData(data: StepCustomerData) {
    customerData.value = data;
  }
  function addOwnerData(data: StepOwnerData) {
    ownerData.value = data;
  }

  return {
    orderRequestData,
    ordererData,
    mphData,
    productData,
    customerData,
    ownerData,
    isOrderingNe4,
    isOrderingProduct,
    isNe4OrderCompleteOrEndAssembly,
    mphTotalUnits,
    ordererAddress,
    salesPartnerEmailAddress,
    netzLeadIdPrefix,
    netzLeadId,
    addOrderRequestData,
    addOrdererData,
    addMphData,
    addProductData,
    addCustomerData,
    addOwnerData,
  };
});

export default useOrderStore;
